import { FC, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IconSearch } from '@tabler/icons-react';
import { Button, IconButton, Select } from '@atoms';
import { EMPTY_FILTERS, PendingCustomersSearchFormValues } from './constants';
import styles from './styles';
import { STATUS_OPTIONS } from '../constants';

interface SearchBoxPendingCustomersProps {
  filters: PendingCustomersSearchFormValues;
  onSearch: (filters: PendingCustomersSearchFormValues) => void;
}

/**
 * Search box to filter pending customers by status
 */
const SearchBoxPendingCustomers: FC<SearchBoxPendingCustomersProps> = ({ filters, onSearch }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const responsive = useMediaQuery(theme.breakpoints.down('md'));

  const formik = useFormik<PendingCustomersSearchFormValues>({
    initialValues: filters,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSearch({ status: values.status });
    },
  });

  const { values, handleBlur, handleChange, handleSubmit, resetForm } = formik;

  /**
   * Clears the search form and resets the applied filters
   */
  const handleClearFilters = () => {
    resetForm({ values: EMPTY_FILTERS });
    onSearch(EMPTY_FILTERS);
  };

  /**
   * Handles the form submit event
   * @param event - The form submit event
   */
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit();
  };

  return (
    <Box sx={styles.searchContainer} component='form' onSubmit={handleFormSubmit}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('PENDING_CUSTOMERS:SEARCH_TITLE')}</Typography>
      </Box>
      <Box sx={styles.content}>
        <Box sx={styles.firstLine(responsive)}>
          <Box sx={styles.field(responsive)}>
            <Select
              label={t('COMMON:STATUS')}
              name='status'
              value={values.status}
              onChange={handleChange}
              onBlur={handleBlur}
              items={STATUS_OPTIONS}
              placeholder='COMMON:SELECT_PLACEHOLDER'
              parseOption={(item: { value: string; label: string }) => ({
                label: t(item.label),
                value: item.value,
              })}
              customStyle={{ width: '100%' }}
            />
          </Box>
          <Box sx={styles.actionsContainer(responsive)}>
            <Button
              customStyle={styles.clearButton}
              text={t('COMMON:CLEAR_FILTER')}
              color='primary'
              variant='link'
              type='button'
              onClick={handleClearFilters}
            />
            <IconButton
              tooltip='COMMON:SEARCH'
              onClick={() => handleSubmit()}
              IconComponent={IconSearch}
              variant='contained'
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBoxPendingCustomers;
